// Site Access view helpers (Site Access overhaul slice D, George approved "fix
// all" 2026-09-23). Pure ESM, no React: the Operations -> Site Access tab, the
// shared compliance modal (components/CrewCompliance.jsx) and the employee
// record's "Cleared for" panel read them. server/test/siteAccessView.test.js
// imports this file and runs it against the real engine's check wording
// (services/crewCompliance.js evaluateEmployee), so a reworded detail line
// that would silently mis-tag a next action fails CI instead.
//
// Everything here reads what the server already sent (e.checks); nothing
// re-judges a verdict. Gray stays gray.
import { csvSafeCell } from './csvSafe.js';

// The checks a lens counts: every check, or only DISA ones in the DISA view.
const viewChecks = (entry, viewMode) => {
  const checks = (entry && entry.checks) || [];
  return viewMode === 'disa' ? checks.filter(c => c.category === 'disa') : checks;
};

export const statusOf = (entry, viewMode) =>
  (entry ? (viewMode === 'disa' ? entry.disa_status : entry.status) : null) || 'gray';

// The chip / pill vocabulary. Gray is "No Rules" everywhere (chip, pill, CSV).
export const STATUS_LABEL = { green: 'Site Ready', amber: 'Expiring', red: 'Not Ready', gray: 'No Rules' };
export const statusLabel = (status, viewMode) =>
  (status === 'green' && viewMode === 'disa') ? 'DISA Clear' : (STATUS_LABEL[status] || STATUS_LABEL.gray);

// Failing then expiring checks: the Missing column.
export function openItems(entry, viewMode) {
  const list = viewChecks(entry, viewMode);
  return [...list.filter(c => c.status === 'fail'), ...list.filter(c => c.status === 'warn')];
}

// A required class ISNetworld decides (evaluateEmployee's "ISN-assigned
// class can only be satisfied by ISN" branch, George 2026-07-29): its detail
// names the ISN assignment. A training record cannot move it, so Quick-Mark
// shows "ISN decides" instead of a checkbox.
export const isIsnGoverned = (check) => !!check && check.category === 'training'
  && /\(.*, via ISNetworld\)|^ASSIGNED to the worker in ISNetworld/.test(String(check.detail || ''));

// Quick-Mark (decision 8). A tick is "employeeId|classIndex" into the grid's
// required classes. quickMarkable = a box ops can usefully tick: not already
// passing, not ISN-governed (only ISN can clear it), not a deactivated
// (sealed) record. liveQuickMarks = the ticks a save may write: the employee
// is still in the grid's scope and the box is still tickable, so a tick left
// over from another site's class list or another job's crew never writes a
// class nobody sees ticked, or training for someone not on screen.
export const quickMarkLabel = (rc) => (rc.code ? `${rc.name} [${rc.code}]` : rc.name);
export const quickMarkCheck = (e, rc) => (e && rc
  ? (e.checks || []).find(c => c.label === quickMarkLabel(rc)) : undefined);
export const quickMarkable = (e, rc) => {
  if (!e || !rc) return false;
  const c = quickMarkCheck(e, rc);
  return !(c && c.status === 'pass') && !isIsnGoverned(c) && !e.inactive_allocated;
};
// The same quick-mark from ONE check in the Site Ready modal (George
// 2026-09-24: "a way to manually tick a class if taken outside our view of
// the safety council"): a failing or expiring required class, not ISN-governed,
// not a sealed record. markRecordFromCheck rebuilds the Quick-Mark grid's
// record (cert_type = the requirement's name, first code alternate) from the
// check's label, so both paths write the same row.
export const modalMarkable = (check, entry) => !!check && check.category === 'training'
  && (check.status === 'fail' || check.status === 'warn')
  && !isIsnGoverned(check) && !(entry && entry.inactive_allocated);
export const markRecordFromCheck = (check) => {
  const code = check && check.course_code ? String(check.course_code).split('/')[0].trim() : '';
  const label = String((check && check.label) || '');
  const name = check && check.course_code ? label.replace(/\s*\[[^\]]*\]\s*$/, '') : label;
  return { cert_type: name.trim(), certificate_no: code || null };
};
export function liveQuickMarks(checked, employees, requiredCerts) {
  const byId = new Map((employees || []).map(e => [String(e.employee_id), e]));
  return [...(checked || [])].filter(k => {
    const [empId, idx] = String(k).split('|');
    return quickMarkable(byId.get(empId), (requiredCerts || [])[Number(idx)]);
  });
}

// What to do about ONE failing check, per the triage George set 2026-07-29
// ("I want to know it's on the employee and not something we failed to
// assign") and the 07-30 Davila lesson: ISN shows it assigned = the worker
// owes it; ISN has no assignment = dispatch must assign it; otherwise the
// missing DISA / TWIC / class record. null for anything that is not a fail.
export function nextAction(check) {
  if (!check || check.status !== 'fail') return null;
  const d = String(check.detail || '');
  const label = String(check.label || '');
  if (/NOT assigned to this worker in ISNetworld/i.test(d)) return { key: 'isn_assign', label: 'Needs ISN assignment' };
  if (/^ASSIGNED\b/.test(d)) {
    const m = d.match(/status ([^.]+)\./);
    return { key: 'isn_owed', label: `Assigned in ISN, not done${m ? ` (${m[1].trim()})` : ''}` };
  }
  if (/EXPIRED/.test(d) && /ISNetworld/.test(d)) return { key: 'isn_expired', label: 'ISN class expired' };
  switch (check.category) {
    case 'isn':
      if (label === 'ISN assignment') return { key: 'isn_email', label: 'Needs email for ISN' };
      if (label === 'ISN number') return { key: 'isn_number', label: 'Missing ISN #' };
      return { key: 'isn_other', label: 'Check ISNetworld' };
    case 'disa':
      if (label === 'DISA drug test' && !/^No drug test/.test(d)) return { key: 'retest', label: 'Retest or site badge' };
      if (/^Expired/.test(d)) return { key: 'disa_expired', label: 'DISA expired' };
      return { key: 'disa', label: 'Missing DISA' };
    case 'twic':
      return { key: 'twic', label: /^Expired/.test(d) ? 'TWIC expired' : 'Missing TWIC' };
    case 'training':
      return /^Expired/.test(d) ? { key: 'class_expired', label: 'Class expired' } : { key: 'class', label: 'Missing class' };
    default:
      return { key: 'other', label: 'See details' };
  }
}

// One row's next actions, deduped with a count, plus the probable-duplicate
// hint when (and only when) the server's detector named a twin
// (roster.probableDuplicates). Never guessed from name text here.
export function rowActions(entry, viewMode) {
  const by = new Map();
  for (const c of viewChecks(entry, viewMode)) {
    const a = nextAction(c);
    if (!a) continue;
    const prev = by.get(a.label);
    by.set(a.label, prev ? { ...prev, count: prev.count + 1 } : { ...a, count: 1 });
  }
  const list = [...by.values()];
  const dup = entry && entry.possible_duplicate_of;
  if (dup && dup.name) list.unshift({ key: 'duplicate', label: `Possible CRM duplicate of ${dup.name}`, count: 1 });
  return list;
}
export const actionsText = (list) => (list || [])
  .map(a => (a.count > 1 ? `${a.label} x${a.count}` : a.label)).join('; ');

// Top blockers: how many of THESE rows fail each check label (a person counts
// once per label). Pass the job-scoped rows, never the whole roster, or the
// counts contradict the pills. Most common first.
export function blockerCounts(entries, viewMode) {
  const by = new Map();
  for (const e of entries || []) {
    const seen = new Set();
    for (const c of viewChecks(e, viewMode)) {
      if (c.status !== 'fail' || seen.has(c.label)) continue;
      seen.add(c.label);
      by.set(c.label, (by.get(c.label) || 0) + 1);
    }
  }
  return [...by.entries()].map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}
export const hasBlocker = (entry, label, viewMode) =>
  viewChecks(entry, viewMode).some(c => c.status === 'fail' && c.label === label);

// Not ready or expiring, with exactly ONE open item (a fail or an expiry):
// one class, test or record away from green.
export const isOneItemAway = (entry, viewMode) => {
  const s = statusOf(entry, viewMode);
  return (s === 'red' || s === 'amber') && openItems(entry, viewMode).length === 1;
};

// Site Access column sort (TableControls compares these as numeric-aware
// strings): not ready first, then expiring, then ready, then no rules; inside
// each, the fewest open items first (closest to ready).
const RANK = { red: 0, amber: 1, green: 2, gray: 3 };
export function statusSortKey(entry, viewMode) {
  const s = statusOf(entry, viewMode);
  const n = viewChecks(entry, viewMode).filter(c => c.status === (s === 'amber' ? 'warn' : 'fail')).length;
  return `${RANK[s] ?? 3}-${String(n).padStart(3, '0')}`;
}

// Where each failing or expiring check gets fixed, in a fixed order:
// badges (a site badge covers only the drug-test age), the DISA tab, the
// Training tab, the ISNetworld tab, the Roster (TWIC and the email ISN needs
// live on the employee's roster / badging profile).
const FIX_ORDER = ['badges', 'disa', 'training', 'isn', 'roster'];
export function fixLinks(entry) {
  const keys = new Set();
  for (const c of openItems(entry)) {
    if (c.category === 'disa') {
      keys.add('disa');
      if (c.label === 'DISA drug test') keys.add('badges');
    } else if (c.category === 'training') {
      if (!isIsnGoverned(c)) keys.add('training');
      if (/ISNetworld/.test(String(c.detail || ''))) keys.add('isn');
    } else if (c.category === 'isn') {
      if (c.label === 'ISN number') keys.add('disa');
      else keys.add('isn');
      if (c.label === 'ISN assignment') keys.add('roster');
    } else if (c.category === 'twic') {
      keys.add('roster');
    }
  }
  return FIX_ORDER.filter(k => keys.has(k));
}

// ---- dates + job dropdown labels ------------------------------------------

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// 'Sep 30, 2026' from YYYY-MM-DD text; '' for blank or anything else.
export function fmtIsoDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  if (!m || !MONTHS[Number(m[2]) - 1]) return '';
  return `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
}

// Job dropdown option text: start date + status ride along so the upcoming
// job is easy to pick (the server already orders upcoming first).
export function jobOptionLabel(j, { withCustomer = false } = {}) {
  if (!j) return '';
  const head = `${withCustomer && j.customer_name ? `${j.customer_name} — ` : ''}${j.site || 'Job'}${j.job_number ? ` · ${j.job_number}` : ''}`;
  const when = j.date_planned ? `starts ${fmtIsoDate(j.date_planned)}` : 'no start date';
  let s = `${head} — ${when}${j.status ? ` · ${j.status}` : ''}`;
  if (Array.isArray(j.employee_ids)) s += ` — ${j.employee_ids.length} assigned`;
  if (Number(j.open_seats) > 0) s += ` + ${j.open_seats} open seat${Number(j.open_seats) === 1 ? '' : 's'}`;
  return s;
}

// ---- URL state (?tab=siteaccess&customer=&site=&job=&through=) -------------

export const SA_PARAM_KEYS = ['customer', 'site', 'job', 'through'];
const ID_RE = /^\d{1,9}$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Sanitized Site Access state from a URLSearchParams. hasSite = a site param
// is present at all: 'site=' (blank) means "customer-wide, on purpose", while
// no site param with a job means "use the job's own site".
export function readSiteAccessParams(params) {
  const get = (k) => String((params && params.get(k)) || '').trim();
  const id = (k) => (ID_RE.test(get(k)) ? get(k) : '');
  return {
    customer: id('customer'), site: id('site'), job: id('job'),
    through: DATE_RE.test(get('through')) ? get('through') : '',
    hasSite: !!(params && params.has('site')),
  };
}

// The params with Site Access state written in. With a job picked the site is
// always written (blank = customer-wide) and through is dropped (the crew's
// own last day onsite is the horizon).
export function writeSiteAccessParams(prev, { customer = '', site = '', job = '', through = '' } = {}) {
  const next = new URLSearchParams(prev);
  for (const k of SA_PARAM_KEYS) next.delete(k);
  if (customer) next.set('customer', String(customer));
  if (job) {
    next.set('site', site ? String(site) : '');
    next.set('job', String(job));
  } else {
    if (site) next.set('site', String(site));
    if (through) next.set('through', String(through));
  }
  return next;
}

// A comparable fingerprint of the Site Access params in a URLSearchParams.
export const saParamsKey = (params) => SA_PARAM_KEYS
  .map(k => `${k}${params && params.has(k) ? `=${params.get(k)}` : ''}`).join('&');

// Operations tab switch: Site Access's params mean nothing on another tab
// (Allocations reads ?job= as ITS drill-in), so crossing into or out of Site
// Access drops them. ?emp= is a one-shot jump (Roster, DISA and Training
// pre-search to it on mount), so any real tab switch drops it too; otherwise
// the next tab would open pre-filtered to someone. The rest stays as it was.
export function tabSwitchParams(prev, currentTab, nextTab) {
  const next = new URLSearchParams(prev);
  if (nextTab !== currentTab) next.delete('emp');
  if (nextTab !== currentTab && (nextTab === 'siteaccess' || currentTab === 'siteaccess')) {
    for (const k of SA_PARAM_KEYS) next.delete(k);
  }
  next.set('tab', nextTab);
  return next;
}

// Deep link into Site Access (Job Hub, Allocations drill-in).
export function siteAccessHref({ customer = '', site = '', job = '', through = '' } = {}) {
  const p = writeSiteAccessParams(new URLSearchParams({ tab: 'siteaccess' }), { customer, site, job, through });
  return `/operations?${p.toString()}`;
}

// ---- CSV export ------------------------------------------------------------

// The rows on screen, in screen order, as an array of arrays (header first).
export function siteAccessCsvRows(rows, { viewMode = 'overall', withJob = false, withAllSites = false } = {}) {
  const head = ['Employee', 'Employee #', 'Craft', 'ISN #'];
  if (withJob) head.push('First Day Onsite', 'Last Day Onsite', 'Verified Through');
  head.push('Status');
  if (withAllSites) head.push('All Sites');
  head.push('Missing', 'Expiring', 'Next Action', 'Notes');
  const out = [head];
  for (const e of rows || []) {
    const checks = viewChecks(e, viewMode);
    const line = [e.name || '', e.employee_no || '', e.craft || '', e.isn_number || ''];
    if (withJob) line.push(e.first_day_onsite || '', e.last_day_onsite || '', e.compliance_through || '');
    line.push(statusLabel(statusOf(e, viewMode), viewMode));
    if (withAllSites) line.push(e.all_sites_status ? STATUS_LABEL[e.all_sites_status] || '' : '');
    line.push(
      checks.filter(c => c.status === 'fail').map(c => c.label).join('; '),
      checks.filter(c => c.status === 'warn').map(c => c.label).join('; '),
      actionsText(rowActions(e, viewMode).filter(a => a.key !== 'duplicate')),
      [e.inactive_allocated ? 'Inactive, still allocated' : '',
       e.possible_duplicate_of && e.possible_duplicate_of.name ? `Possible CRM duplicate of ${e.possible_duplicate_of.name}` : '']
        .filter(Boolean).join('; '),
    );
    out.push(line);
  }
  return out;
}

// CSV text: every cell through csvSafeCell FIRST (formula-injection guard, the
// 2026-09-05 hardening), then ordinary quoting; BOM so Excel reads UTF-8.
export function toCsv(rows) {
  const esc = (v) => {
    const s = String(csvSafeCell(v == null ? '' : v));
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return String.fromCharCode(0xFEFF) + (rows || []).map(r => r.map(esc).join(',')).join('\r\n');
}
